import { useQuery } from '@tanstack/react-query';
import { apiFetch, apiSearch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

/** `POST /client-lookup` (109): who holds a client, for any client, by name. */
export interface LookupRow {
  /** Only for a client the caller can already open; otherwise null and nothing links. */
  client_id: string | null;
  client_ref: string;
  display_name: string;
  legal_status: string | null;
  case_manager: string | null;
  finance_case_manager: string | null;
  covering: string | null;
}

/** Three letters of a name, spaces aside, as the database asks. */
export const LOOKUP_MIN_LETTERS = 3;

export function lookupLetters(q: string): number {
  return q.replace(/\s+/g, '').length;
}

/** Whether to offer the lookup at all: case managers (Social Work and Finance) and above. */
export function useLookupPermitted() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client-lookup', 'permitted'],
    queryFn: () => apiFetch<{ permitted: boolean }>('/client-lookup/permitted', getToken),
    ...queryDefaults,
    staleTime: 10 * 60_000,
  });
}

/**
 * Runs only when asked (`asked` is the text the person asked about), never as
 * they type: every lookup is recorded against the person asking, so each one
 * should be a question somebody meant.
 */
export function useClientLookup(asked: string | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client-lookup', asked],
    queryFn: () =>
      apiSearch<{ clients: LookupRow[]; more: boolean }>('/client-lookup', { q: asked ?? '' }, getToken),
    ...queryDefaults,
    enabled: asked !== null,
    // Never asked again behind the person's back: each fetch is an audit row.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}
